import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Building2, DollarSign, FileText, Download, Clock, User,
  Lock, AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Eye, EyeOff,
  Loader2, ExternalLink, Paperclip
} from 'lucide-react';
import axios from 'axios';
import BalanceSheetCard from './BalanceSheetCard';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_LABELS = {
  sba_loan: 'SBA Loan',
  term_loan: 'Term Loan',
  term_loan_10yr: '10 Year Term Loan',
  term_loan_3_5yr: 'EZ Term Loan 3-5 Yr',
  term_loc_hybrid: 'Term Loan / LOC Hybrid',
  line_of_credit: 'Line of Credit',
  equipment_financing: 'Equipment Financing',
  merchant_processing: 'Merchant Processing',
  jumbo_term_loan: 'Jumbo Term Loan / LOC',
  transportation_factoring: 'Transportation Factoring',
  real_estate_financing: 'Real Estate Financing',
  foreclosure_bailout: 'Foreclosure Bailout',
};

const FIELD_LABELS = {
  legal_corporate_name: 'Legal Corporate Name',
  business_legal_name: 'Business Legal Name',
  business_name: 'Business Name',
  doing_business_as: 'DBA',
  entity_type: 'Entity Type',
  business_structure: 'Business Structure',
  federal_tax_id: 'Federal Tax ID',
  business_address: 'Business Address',
  business_city: 'City',
  business_state: 'State',
  business_zip: 'ZIP',
  business_phone: 'Business Phone',
  company_website: 'Company Website',
  business_start_date: 'Business Start Date',
  date_business_started: 'Date Business Started',
  industry_type: 'Industry',
  naics_code: 'NAICS Code',
  naics_code_or_nature: 'NAICS / Nature of Business',
  number_of_employees: 'Employees',
  annual_revenue: 'Annual Revenue',
  monthly_revenue: 'Monthly Revenue',
  total_monthly_sales: 'Total Monthly Sales',
  funding_amount_requested: 'Funding Amount Requested',
  loan_requested_amount: 'Loan Amount Requested',
  requested_line_of_credit_amount: 'Requested LOC Amount',
  amount_requested: 'Amount Requested',
  first_name: 'First Name',
  last_name: 'Last Name',
  mc_number: 'MC Number',
  email: 'Email',
  phone_number: 'Phone Number',
  business_dba_name: 'Business DBA Name',
  type_of_business_entity: 'Business Entity Type',
  state_of_incorporation: 'State of Incorporation',
  business_apt_suite: 'Suite / Apt',
  business_federal_tax_id: 'Federal Tax ID',
  website: 'Website',
  owner_first_name: 'Owner First Name',
  owner_last_name: 'Owner Last Name',
  owner_dob: 'Date of Birth',
  owner_street_address: 'Home Address',
  owner_apt_suite: 'Suite / Apt',
  owner_city: 'City',
  owner_state: 'State',
  owner_zip: 'ZIP',
  owner_ssn: 'Social Security #',
  owner_email: 'Owner Email',
  owner_phone: 'Owner Phone',
  business_type: 'Business Type',
  total_loan_amount: 'Total Loan Amount',
  use_of_proceeds: 'Use of Proceeds',
  use_of_funds_description: 'Use of Funds',
  first_name: 'First Name',
  last_name: 'Last Name',
  first_last_name: 'Owner Name',
  owner_name: 'Owner Name',
  ownership_percentage: 'Ownership %',
  email_address: 'Email',
  phone_number: 'Phone',
  home_address: 'Home Address',
  date_of_birth: 'Date of Birth',
  social_security_number: 'SSN',
  ssn_itin: 'SSN/ITIN',
  collateral: 'Collateral',
  landlord_name: 'Landlord Name',
  monthly_rent: 'Monthly Rent',
  average_daily_balance: 'Avg Daily Balance',
  average_annual_revenue: 'Avg Annual Revenue',
};

const SENSITIVE_FIELDS = ['social_security_number', 'ssn_itin', 'owner_ssn'];

const maskSSN = (value) => {
  const str = String(value);
  if (str.length <= 4) return '***-**-' + str;
  return '***-**-' + str.slice(-4);
};

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return null;
  if (SENSITIVE_FIELDS.includes(key)) return value; // SSN handled by SensitiveField component
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return null; // handled separately
  if (typeof value === 'object') return null;
  if (typeof value === 'number' && (key.includes('amount') || key.includes('revenue') || key.includes('sales') || key.includes('rent') || key.includes('balance')))
    return `$${value.toLocaleString()}`;
  return String(value);
};

const SensitiveField = ({ value }) => {
  const [visible, setVisible] = useState(false);
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-medium">{visible ? String(value) : maskSSN(value)}</span>
      <button
        onClick={() => setVisible(!visible)}
        className="text-slate-400 hover:text-slate-600 transition-colors p-0.5 rounded"
        title={visible ? 'Hide SSN' : 'Show SSN'}
        data-testid="ssn-toggle"
      >
        {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
};

const SharedUnderwritingView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingPreApp, setDownloadingPreApp] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/api/shared/underwriting/${token}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load shared data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggleSection = (key) => setExpandedSections(p => ({ ...p, [key]: !p[key] }));

  const downloadDoc = async (docId, filename) => {
    if (!docId || downloadingDoc) return; // prevent duplicate clicks while a download is in flight
    setDownloadingDoc(docId);
    try {
      const res = await axios.get(
        `${API}/api/shared/underwriting/${token}/document/${docId}`,
        { responseType: 'blob', timeout: 120000 }
      );
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.style.display = 'none';
      document.body.appendChild(a); // attach to DOM so click() reliably fires across browsers
      a.click();
      // Detach and revoke on the next tick so the browser has time to start the download
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_e) { /* already removed */ }
        window.URL.revokeObjectURL(url);
      }, 250);
    } catch (err) {
      alert('Failed to download document. Please try again.');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const downloadAllAsZip = async () => {
    if (downloadingAll) return;
    setDownloadingAll(true);
    try {
      const res = await axios.get(
        `${API}/api/shared/underwriting/${token}/download-all`,
        { responseType: 'blob', timeout: 600000 } // up to 10 minutes for large bundles
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      // Prefer server-provided filename from Content-Disposition
      let filename = 'Underwriting Package.zip';
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (cd) {
        const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      } else if (data?.business_name) {
        filename = `${data.business_name} - Underwriting Package.zip`;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_e) { /* already removed */ }
        window.URL.revokeObjectURL(url);
      }, 250);
    } catch (err) {
      const msg = err?.response?.status === 404
        ? 'No documents available to download.'
        : 'Failed to prepare ZIP. Please try again.';
      alert(msg);
    } finally {
      setDownloadingAll(false);
    }
  };

  const [downloadingDebtPdf, setDownloadingDebtPdf] = useState(false);

  const downloadDebtSchedulePdf = async () => {
    if (downloadingDebtPdf) return;
    setDownloadingDebtPdf(true);
    try {
      const res = await axios.get(
        `${API}/api/shared/underwriting/${token}/debt-schedule/pdf`,
        { responseType: 'blob', timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debt_schedule_${data?.business_name || data?.id || 'application'}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_e) { /* already removed */ }
        window.URL.revokeObjectURL(url);
      }, 250);
    } catch (err) {
      alert('Failed to download Debt Schedule PDF. Please try again.');
    } finally {
      setDownloadingDebtPdf(false);
    }
  };

  const downloadPreAppPdf = async () => {
    if (downloadingPreApp) return;
    setDownloadingPreApp(true);
    try {
      const res = await axios.get(
        `${API}/api/shared/underwriting/${token}/merchant-pre-app/pdf`,
        { responseType: 'blob', timeout: 120000 }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      // Use server-provided filename if present
      let filename = `Pre Merchant Processing Application.pdf`;
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (cd) {
        const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_e) { /* already removed */ }
        window.URL.revokeObjectURL(url);
      }, 250);
    } catch (err) {
      const msg = err?.response?.status === 404
        ? 'Pre-MPA PDF is not yet available.'
        : 'Failed to download Pre-MPA PDF. Please try again.';
      alert(msg);
    } finally {
      setDownloadingPreApp(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-[#081120] border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-3">
          {error.includes('deactivated') ? (
            <Lock className="h-12 w-12 text-slate-400 mx-auto" />
          ) : (
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
          )}
          <h2 className="text-lg font-semibold text-slate-800">{error.includes('deactivated') ? 'Link Deactivated' : 'Link Not Found'}</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const loanData = data.loan_data || {};

  // Define field sections for organized display
  const SHARED_SECTIONS = {
    term_loan: [
      { title: 'Primary Borrower', keys: ['first_name', 'last_name', 'email_address', 'phone_number', 'date_of_birth', 'social_security_number', 'ownership_percentage', 'home_address'] },
      { title: 'Business Information', keys: ['legal_corporate_name', 'doing_business_as', 'business_address', 'business_city', 'business_state', 'business_zip', 'entity_type', 'federal_tax_id', 'industry_type', 'business_start_date', 'state_of_incorporation', 'number_of_employees'] },
      { title: 'Financial Details', keys: ['annual_revenue', 'monthly_revenue', 'gross_annual_sales', 'funding_amount_requested', 'use_of_funds', 'use_of_funds_description'] },
    ],
    term_loan_10yr: [
      { title: 'Primary Borrower', keys: ['first_name', 'last_name', 'email_address', 'phone_number', 'date_of_birth', 'social_security_number', 'ownership_percentage', 'home_address'] },
      { title: 'Business Information', keys: ['legal_corporate_name', 'doing_business_as', 'business_address', 'business_city', 'business_state', 'business_zip', 'entity_type', 'federal_tax_id', 'industry_type', 'business_start_date', 'state_of_incorporation', 'number_of_employees'] },
      { title: 'Financial Details', keys: ['annual_revenue', 'monthly_revenue', 'funding_amount_requested', 'use_of_funds'] },
    ],
    term_loc_hybrid: [
      { title: 'Loan Details', keys: [
        { key: 'total_loan_amount', fallback: 'funding_amount_requested', label: 'Loan Amount' },
        'use_of_proceeds',
        'use_of_funds',
      ]},
      { title: 'Business Information', keys: [
        { key: 'business_name', fallback: 'legal_corporate_name', label: 'Business Name' },
        'doing_business_as',
        { key: 'business_structure', fallback: 'entity_type', label: 'Business Structure' },
        { key: 'average_annual_revenue', fallback: 'annual_revenue', label: 'Annual Revenue' },
        'monthly_revenue',
        { key: 'date_established', fallback: 'business_start_date', label: 'Date Established' },
        'federal_tax_id', 'business_phone', 'company_website',
        { key: 'naics_code', fallback: 'industry_type', label: 'Industry / NAICS Code' },
        'state_of_incorporation', 'number_of_employees',
      ]},
      { title: 'Business Address', keys: ['business_address', 'business_city', 'business_state', 'business_zip'] },
      { title: 'Role at Business', keys: ['role_at_business', 'title_at_company', 'ownership_percentage', 'years_with_business', 'years_industry_experience'] },
      { title: 'Personal Details', keys: [
        'first_name', 'last_name', 'email_address', 'phone_number',
        { key: 'ssn_itin', fallback: 'social_security_number', label: 'SSN' },
        'date_of_birth', 'place_of_birth',
      ]},
      { title: 'Identification', keys: ['id_type', 'id_number', 'id_state_issued', 'id_expiration_date'] },
      { title: 'Home Address', keys: ['owns_residence', 'home_address', 'home_city', 'home_state', 'home_zip'] },
      { title: 'Other', keys: ['citizenship_status', 'other_business_interests'] },
    ],
    term_loan_3_5yr: [
      { title: 'Personal Details', keys: ['first_name', 'last_name', 'email_address', 'phone_number', 'ssn_itin', 'date_of_birth', 'place_of_birth', 'ownership_percentage'] },
      { title: 'Home Address', keys: ['owns_residence', 'home_address', 'home_city', 'home_state', 'home_zip'] },
      { title: 'Business Details', keys: ['business_name', 'doing_business_as', 'business_structure', 'date_established', 'federal_tax_id', 'business_phone', 'company_website', 'naics_code', 'average_annual_revenue'] },
      { title: 'Business Address', keys: ['business_address', 'business_city', 'business_state', 'business_zip'] },
      { title: 'Role at Business', keys: ['role_at_business', 'title_at_company', 'years_with_business', 'years_industry_experience'] },
      { title: 'Identification', keys: ['id_type', 'id_number', 'id_state_issued', 'id_expiration_date', 'citizenship_status'] },
      { title: 'Loan Details', keys: ['use_of_proceeds', 'use_of_proceeds_amounts', 'total_loan_amount'] },
    ],
    sba_loan: [
      { title: 'Primary Applicant', keys: ['first_last_name', 'email_address', 'mobile_phone_number', 'date_of_birth', 'social_security_number', 'home_address', 'ownership_percentage', 'annual_income'] },
      { title: 'Business Information', keys: ['business_legal_name', 'doing_business_as', 'legal_entity_type', 'business_start_date', 'federal_tax_id', 'business_phone_number', 'business_address', 'naics_code_or_nature'] },
      { title: 'Loan Details', keys: ['loan_requested_amount', 'has_existing_debt', 'refinance_existing_debt', 'total_monthly_sales'] },
      { title: 'Assets', keys: ['assets_cash', 'assets_home', 'assets_automobile', 'assets_investment_accounts', 'assets_other'] },
      { title: 'Liabilities', keys: ['liabilities_mortgage', 'liabilities_credit_cards', 'liabilities_auto_loans', 'liabilities_other'] },
    ],
    line_of_credit: [
      { title: 'Owner Information', keys: ['owner_name', 'owner_email', 'owner_phone', 'owner_ssn', 'owner_address'] },
      { title: 'Business Information', keys: ['business_name', 'business_industry', 'years_in_business', 'business_zip_code'] },
      { title: 'Credit Details', keys: ['requested_line_of_credit_amount', 'purpose_of_credit'] },
    ],
    equipment_financing: [
      { title: 'Owner Information', keys: ['owner_name', 'owner_email', 'owner_phone', 'owner_ssn', 'owner_dob', 'owner_address', 'ownership_percentage', 'fico_score'] },
      { title: 'Business Information', keys: ['business_name', 'business_address', 'business_city', 'business_state', 'business_zip', 'entity_type', 'federal_tax_id', 'years_in_business', 'industry', 'annual_revenue'] },
      { title: 'Equipment Details', keys: ['equipment_type', 'equipment_description', 'equipment_cost', 'new_or_used', 'vendor_name', 'vendor_contact'] },
      { title: 'Financing Details', keys: ['amount_requested', 'down_payment', 'desired_term_months', 'use_of_equipment'] },
    ],
    jumbo_term_loan: [
      { title: 'Business Information', keys: ['business_legal_name', 'business_dba_name', 'type_of_business_entity', 'industry_type', 'state_of_incorporation', 'business_street_address', 'business_apt_suite', 'business_city', 'business_state', 'business_zip', 'business_federal_tax_id', 'business_start_date', 'website', 'amount_requested'] },
      { title: 'Owner Information', keys: ['owner_first_name', 'owner_last_name', 'owner_dob', 'owner_street_address', 'owner_apt_suite', 'owner_city', 'owner_state', 'owner_zip', 'owner_ssn'] },
    ],
    merchant_processing: [
      { title: 'Contact Information', keys: ['contact_name', 'company_name', 'industry_type', 'website_url', 'years_in_business'] },
      { title: 'Point of Sale Details', keys: ['pos_software_or_terminals', 'pos_software_name', 'number_of_terminals', 'terminal_brand_model'] },
    ],
    transportation_factoring: [
      { title: 'Applicant Information', keys: ['first_name', 'last_name', 'mc_number', 'email', 'phone_number'] },
    ],
    real_estate_financing: [
      { title: 'Request for Financing', keys: [
        { key: 'financing_amount_requested', label: 'Financing Amount Requested' },
        { key: 'down_payment_amount', label: 'Down Payment Amount' },
        'use_of_financing',
        { key: 'detailed_use_of_funds', label: 'Detailed Use of Funds' },
      ]},
      { title: 'Borrower', keys: [
        { key: 'borrowers_name', label: "Borrower's Name" },
      ]},
      { title: 'Business Information', keys: [
        { key: 'legal_name_of_business', label: 'Legal Name of Business' },
        { key: 'business_in_good_standing', label: 'Business in Good Standing?' },
        { key: 'date_established', label: 'Date Established' },
        { key: 'tax_id', label: 'Tax ID (EIN)' },
        { key: 'business_type', label: 'Business Type' },
        { key: 'business_address', label: 'Business Address' },
        { key: 'business_city_state_zip', label: 'Business City, State, ZIP' },
        { key: 'mailing_address', label: 'Mailing Address' },
        { key: 'mailing_city_state_zip', label: 'Mailing City, State, ZIP' },
        { key: 'main_phone', label: 'Main Phone' },
        { key: 'fax', label: 'Fax' },
        { key: 'number_of_employees', label: '# of Employees' },
        { key: 'business_email', label: 'Business Email' },
        { key: 'type_of_business', label: 'Type of Business' },
      ]},
      { title: 'Property Information', keys: [
        { key: 'property_address', label: 'Property Address' },
        { key: 'property_type', label: 'Property Type' },
        { key: 'number_of_units', label: 'Number of Units' },
        { key: 'total_sqft', label: 'Total SQFT' },
        { key: 'purchase_price', label: 'Purchase Price' },
        { key: 'property_under_contract', label: 'Property Under Contract?' },
        { key: 'original_purchase_price', label: 'Original Purchase Price (if already owned)' },
        { key: 'original_purchase_date', label: 'Original Purchase Date (if already owned)' },
        { key: 'existing_payoff_amount', label: 'Existing Payoff Amount (if already owned)' },
        { key: 'as_is_value', label: 'As-Is Value' },
        { key: 'rehab_budget', label: 'Rehab Budget' },
        { key: 'estimated_after_repair_value', label: 'Estimated After Repair Value (ARV)' },
      ]},
      { title: 'Financial Information', keys: [
        { key: 'liquidity_cash_to_close', label: 'Liquidity / Cash to Close' },
        { key: 'exit_strategy', label: 'Exit Strategy' },
        { key: 'monthly_rental_income', label: 'Monthly Rental Income (if Refinance)' },
        { key: 'annual_taxes', label: 'Annual Taxes' },
        { key: 'annual_insurance', label: 'Annual Insurance' },
        { key: 'estimated_middle_fico', label: 'Estimated Middle FICO Score' },
      ]},
      { title: 'Experience', keys: [
        { key: 'completed_rehab_projects_3yr', label: 'Completed Rehab Projects (Past 3 Years)' },
      ]},
      { title: 'Additional Information', keys: [
        { key: 'additional_notes', label: 'Additional Notes' },
      ]},
    ],
    foreclosure_bailout: [
      { title: 'Property Type', keys: [
        { key: 'property_type', label: 'Property Type' },
      ]},
      { title: 'Property Location', keys: [
        { key: 'property_state', label: 'State' },
      ]},
      { title: 'Property / Loan Information', keys: [
        { key: 'estimated_property_value', label: 'Estimated Property Value' },
        { key: 'total_mortgage_balances', label: 'Total Amount of Mortgage Balances' },
        { key: 'loan_amount', label: 'Loan Amount' },
      ]},
      { title: 'Foreclosure Information', keys: [
        { key: 'foreclosure_sale_scheduled', label: 'Is a foreclosure sale or auction date scheduled?' },
        { key: 'foreclosure_sale_date', label: 'Foreclosure sale / auction date' },
      ]},
      { title: 'Borrower Information', keys: [
        { key: 'business_name', label: 'Business Name' },
        { key: 'borrower_first_name', label: 'Borrower First Name' },
        { key: 'borrower_last_name', label: 'Borrower Last Name' },
        { key: 'borrower_email', label: 'Borrower Email' },
        { key: 'borrower_phone', label: 'Borrower Phone' },
      ]},
      { title: 'Vesting', keys: [
        { key: 'title_held_in_entity', label: 'Is the title held in an entity?' },
        { key: 'entity_name', label: 'Entity Name' },
        { key: 'entity_ein', label: 'Entity EIN' },
      ]},
      { title: 'Loan Structure', keys: [
        { key: 'payment_structure', label: 'Payment Structure' },
        { key: 'broker_fee_percent', label: 'Broker Fee (%)' },
      ]},
    ],
  };

  const sections = SHARED_SECTIONS[data.product_type];
  
  // Build organized fields or fall back to flat list
  const loanFields = Object.entries(loanData).filter(([k, v]) => {
    if (Array.isArray(v) || typeof v === 'object') return false;
    return formatValue(k, v) !== null;
  });

  const additionalOwners = loanData.additional_owners || [];

  const renderField = (key, value, labelOverride) => {
    const hasValue = value !== null && value !== undefined && value !== '';
    return (
      <div key={key} className="py-1.5">
        <dt className="text-xs text-slate-500 font-medium uppercase tracking-wide">{labelOverride || FIELD_LABELS[key] || key.replace(/_/g, ' ')}</dt>
        <dd className={`text-sm font-medium mt-0.5 ${hasValue ? 'text-slate-800' : 'text-slate-300'}`}>
          {hasValue
            ? (SENSITIVE_FIELDS.includes(key) ? <SensitiveField value={value} /> : formatValue(key, value))
            : '—'
          }
        </dd>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-underwriting-view">
      {/* Header */}
      <div className="bg-[#081120] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-3 mb-1">
            <img src="/assets/quidity-logo-light.png" alt="Quidity" className="h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="text-sm font-medium text-slate-400">Shared Underwriting File</span>
          </div>
          <h1 className="text-2xl font-bold mt-3" data-testid="shared-biz-name">{data.business_name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-300">
            <span>{PRODUCT_LABELS[data.product_type] || data.product_type}</span>
            <span>|</span>
            <span className="font-medium text-white">${Number(data.amount_requested || 0).toLocaleString()}</span>
            <span>|</span>
            <Badge className="bg-white/10 text-white border-white/20 text-xs">{(data.status || '').replace(/_/g, ' ')}</Badge>
          </div>
          <p className="text-xs text-slate-400 mt-2">Shared by {data.shared_by} on {new Date(data.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Application Data Fields */}
        <Card data-testid="shared-loan-data">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('fields')}>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Application Details
              {expandedSections.fields === false ? <ChevronRight className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.fields !== false && (
            <CardContent>
              {sections ? (
                <div className="space-y-6">
                  {sections.map((section) => (
                    <div key={section.title}>
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 pb-1 border-b border-slate-100">{section.title}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                        {section.keys.map(entry => {
                          if (typeof entry === 'object' && entry.key) {
                            // Merged field: use primary key, fall back to secondary
                            const val = loanData[entry.key] ?? loanData[entry.fallback] ?? undefined;
                            return renderField(entry.key, val, entry.label);
                          }
                          return renderField(entry, loanData[entry]);
                        })}
                      </div>
                    </div>
                  ))}
                  {/* Render any remaining filled fields not in sections */}
                  {(() => {
                    const allSectionKeys = new Set();
                    sections.forEach(s => s.keys.forEach(entry => {
                      if (typeof entry === 'object') { allSectionKeys.add(entry.key); if (entry.fallback) allSectionKeys.add(entry.fallback); }
                      else allSectionKeys.add(entry);
                    }));
                    const remaining = loanFields.filter(([k]) => !allSectionKeys.has(k));
                    if (remaining.length === 0) return null;
                    return (
                      <div>
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 pb-1 border-b border-slate-100">Other Details</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                          {remaining.map(([k, v]) => renderField(k, v))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                  {loanFields.map(([key, value]) => renderField(key, value))}
                </div>
              )}
              {additionalOwners.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Additional Owners</h4>
                  {additionalOwners.map((owner, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg mb-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                      {Object.entries(owner).filter(([, v]) => v).map(([k, v]) => (
                        <div key={k}>
                          <span className="text-xs text-slate-500">{FIELD_LABELS[k] || k.replace(/_/g, ' ')}</span>
                          <p className="font-medium text-slate-700">
                            {SENSITIVE_FIELDS.includes(k) ? <SensitiveField value={v} /> : formatValue(k, v)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Debt Schedule */}
        {data.debt_schedule?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle
                  className="text-base flex items-center gap-2 cursor-pointer flex-1 min-w-0"
                  onClick={() => toggleSection('debt')}
                >
                  <DollarSign className="h-4 w-4 shrink-0" />
                  <span className="truncate">Debt Schedule ({data.debt_schedule.length})</span>
                  {expandedSections.debt === false ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                </CardTitle>
                <Button
                  size="sm"
                  onClick={downloadDebtSchedulePdf}
                  disabled={downloadingDebtPdf}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  data-testid="shared-debt-schedule-download-btn"
                  title="Download the full Business Debt Schedule as a PDF"
                >
                  {downloadingDebtPdf ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Preparing PDF...</>
                  ) : (
                    <><Download className="h-4 w-4" /> Download PDF</>
                  )}
                </Button>
              </div>
            </CardHeader>
            {expandedSections.debt !== false && (
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-xs text-slate-500">
                      <th className="pb-2 pr-3">Lender</th>
                      <th className="pb-2 pr-3">Type</th>
                      <th className="pb-2 pr-3 text-right">Original Amt</th>
                      <th className="pb-2 pr-3 text-right">Balance</th>
                      <th className="pb-2 pr-3 text-right">Rate</th>
                      <th className="pb-2 pr-3 text-right">Payment</th>
                      <th className="pb-2 pr-3">Secured</th>
                      <th className="pb-2 pr-3">Status</th>
                      <th className="pb-2 pr-3">Origin</th>
                      <th className="pb-2 pr-3">Maturity</th>
                    </tr></thead>
                    <tbody>
                      {data.debt_schedule.map((d, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-3 font-medium">{d.lender_name || d.creditor || '—'}</td>
                          <td className="py-2 pr-3 text-slate-600">{d.type_of_debt || d.loan_type || d.type || '—'}</td>
                          <td className="py-2 pr-3 text-right">{d.original_amount ? `$${Number(d.original_amount).toLocaleString()}` : '—'}</td>
                          <td className="py-2 pr-3 text-right">{d.current_balance || d.balance ? `$${Number(d.current_balance || d.balance || 0).toLocaleString()}` : '—'}</td>
                          <td className="py-2 pr-3 text-right">{d.interest_rate != null ? `${d.interest_rate}%` : '—'}</td>
                          <td className="py-2 pr-3 text-right">{d.monthly_payment ? `$${Number(d.monthly_payment).toLocaleString()}/mo` : '—'}</td>
                          <td className="py-2 pr-3 text-slate-600">{d.secured_or_unsecured || '—'}</td>
                          <td className="py-2 pr-3 text-slate-600">{d.current_or_delinquent || '—'}</td>
                          <td className="py-2 pr-3 text-slate-600">{d.open_date || '—'}</td>
                          <td className="py-2 pr-3 text-slate-600">{d.maturity_date || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Balance Sheet (read-only) */}
        {data.balance_sheet_data && Object.keys(data.balance_sheet_data || {}).length > 0 && (
          <Card data-testid="shared-balance-sheet-section">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle
                  className="text-base flex items-center gap-2 cursor-pointer flex-1 min-w-0"
                  onClick={() => toggleSection('balance_sheet')}
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="truncate">Balance Sheet</span>
                  {expandedSections.balance_sheet === false ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                </CardTitle>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/shared/underwriting/${token}/balance-sheet/pdf`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white shrink-0"
                  data-testid="shared-bs-download-pdf-btn"
                  title="Download the Balance Sheet as a PDF"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
              </div>
            </CardHeader>
            {expandedSections.balance_sheet !== false && (
              <CardContent>
                <BalanceSheetCard
                  sharedUnderwritingToken={token}
                  data={data.balance_sheet_data}
                  uploadedFiles={data.balance_sheet_files || []}
                  readOnly={true}
                />
              </CardContent>
            )}
          </Card>
        )}

        {/* Personal Financial Statement — per-owner PDF downloads */}
        {Array.isArray(data.pfs_entries) && data.pfs_entries.length > 0 && (
          <Card data-testid="shared-pfs-section">
            <CardHeader className="pb-2">
              <CardTitle
                className="text-base flex items-center gap-2 cursor-pointer"
                onClick={() => toggleSection('pfs')}
              >
                <User className="h-4 w-4 shrink-0" />
                <span className="truncate">Personal Financial Statement{data.pfs_entries.length > 1 ? `s (${data.pfs_entries.length})` : ''}</span>
                {expandedSections.pfs === false ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.pfs !== false && (
              <CardContent>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                  {data.pfs_entries.map((entry) => (
                    <div key={entry.pfs_id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`shared-pfs-row-${entry.pfs_id}`}>
                      <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{entry.owner_name || 'Owner'}</div>
                        <div className="text-xs text-slate-500">
                          {entry.has_data ? 'Personal Financial Statement — PDF ready' : 'PFS entry has no financial data yet'}
                        </div>
                      </div>
                      {entry.has_data ? (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/shared/underwriting/${token}/pfs/${entry.pfs_id}/pdf`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white shrink-0"
                          data-testid={`shared-pfs-download-btn-${entry.pfs_id}`}
                          title="Download this owner's Personal Financial Statement PDF"
                        >
                          <Download className="h-4 w-4" /> Download PDF
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400 shrink-0" data-testid={`shared-pfs-empty-${entry.pfs_id}`}>No data</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Use of Funds Schedule (read-only) */}
        {data.use_of_funds_schedule?.length > 0 && (
          <Card data-testid="shared-uof-section">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle
                  className="text-base flex items-center gap-2 cursor-pointer flex-1 min-w-0"
                  onClick={() => toggleSection('uof')}
                >
                  <DollarSign className="h-4 w-4 shrink-0" />
                  <span className="truncate">Use of Funds Schedule ({data.use_of_funds_schedule.length})</span>
                  {expandedSections.uof === false ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                </CardTitle>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/shared/underwriting/${token}/use-of-funds/pdf`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  data-testid="shared-uof-download-pdf-btn"
                  title="Download the Use of Funds Schedule as a PDF"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
              </div>
            </CardHeader>
            {expandedSections.uof !== false && (
              <CardContent>
                <div className="space-y-2">
                  {data.use_of_funds_schedule.map((it) => (
                    <div key={it.id} className="bg-white border border-emerald-200 rounded-lg p-3" data-testid={`shared-uof-row-${it.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">{it.description || <span className="italic text-slate-400">No description</span>}</span>
                            {it.category && (
                              <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-semibold">{it.category}</span>
                            )}
                          </div>
                          {it.notes && <p className="text-xs text-slate-500 mt-1">{it.notes}</p>}
                        </div>
                        <div className="text-sm font-semibold text-emerald-700 shrink-0">
                          {it.amount != null ? `$${Number(it.amount).toLocaleString(undefined, {maximumFractionDigits: 2})}` : '—'}
                        </div>
                      </div>
                      {(it.attachments || []).length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-slate-100 mt-2">
                          <Paperclip className="h-3 w-3 text-slate-400" />
                          {it.attachments.map(a => (
                            <a
                              key={a.file_id}
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/shared/underwriting/${token}/use-of-funds/${it.id}/attachment/${a.file_id}`}
                              className="text-[11px] px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1 max-w-[220px] truncate"
                              data-testid={`shared-uof-attachment-${a.file_id}`}
                              title={a.filename}
                            >
                              <FileText className="h-3 w-3 shrink-0" />
                              <span className="truncate">{a.filename}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-end pt-1">
                    <div className="text-sm font-bold text-emerald-900">
                      Total: ${data.use_of_funds_schedule.reduce((s, it) => s + (Number(it.amount) || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Borrower Submissions / Custom Responses */}
        {data.borrower_submissions?.length > 0 && (
          <Card>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('submissions')}>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Borrower Information ({data.borrower_submissions.length})
                {expandedSections.submissions === false ? <ChevronRight className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.submissions !== false && (
              <CardContent className="space-y-3">
                {data.borrower_submissions.map((sub, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Submitted {new Date(sub.submitted_at).toLocaleDateString()}</p>
                    {sub.custom_responses?.map((cr, ci) => (
                      <div key={ci} className="mb-2">
                        <p className="text-xs font-medium text-slate-600">{cr.label}</p>
                        <p className="text-sm text-slate-800 mt-0.5">{cr.response}</p>
                      </div>
                    ))}
                    {sub.fields_updated?.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1">Fields updated: {sub.fields_updated.join(', ')}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Pre Merchant Processing Application (MP only) — read-only summary
            with a Download PDF action mirroring the in-portal review card. */}
        {data.product_type === 'merchant_processing' && data.merchant_pre_app_data && (
          (() => {
            const pre = data.merchant_pre_app_data || {};
            const filled = !!(
              pre.legal_business_name || pre.dba_name || pre.federal_tax_id ||
              pre.business_phone_number || pre.email_address ||
              (pre.owner1 && (pre.owner1.primary_owner_name || pre.owner1.social_security_number))
            );
            if (!filled) return null;
            const ownerName = pre.owner1?.primary_owner_name;
            return (
              <Card className="border-emerald-200 bg-emerald-50/40" data-testid="shared-mp-pre-app-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                      <FileText className="h-4 w-4 text-emerald-700" />
                      Pre Merchant Processing Application
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">Saved</span>
                    </CardTitle>
                    <Button
                      size="sm"
                      onClick={downloadPreAppPdf}
                      disabled={downloadingPreApp}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="shared-mp-pre-app-download-btn"
                      title="Download the completed Pre-MPA as a PDF"
                    >
                      {downloadingPreApp ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Preparing PDF...</>
                      ) : (
                        <><Download className="h-4 w-4" /> Download PDF</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    {pre.legal_business_name && <div><span className="font-semibold text-slate-700">Legal Business Name:</span> <span className="text-slate-800">{pre.legal_business_name}</span></div>}
                    {pre.dba_name && <div><span className="font-semibold text-slate-700">DBA:</span> <span className="text-slate-800">{pre.dba_name}</span></div>}
                    {pre.federal_tax_id && <div><span className="font-semibold text-slate-700">Federal Tax ID:</span> <span className="text-slate-800">{pre.federal_tax_id}</span></div>}
                    {pre.business_phone_number && <div><span className="font-semibold text-slate-700">Business Phone:</span> <span className="text-slate-800">{pre.business_phone_number}</span></div>}
                    {pre.email_address && <div><span className="font-semibold text-slate-700">Email:</span> <span className="text-slate-800 break-all">{pre.email_address}</span></div>}
                    {pre.mail_to && <div><span className="font-semibold text-slate-700">Mail To:</span> <span className="text-slate-800">{pre.mail_to}</span></div>}
                    {pre.physical_street_address && <div className="md:col-span-2"><span className="font-semibold text-slate-700">Address:</span> <span className="text-slate-800">{[pre.physical_street_address, pre.city, pre.state, pre.zip].filter(Boolean).join(', ')}</span></div>}
                    {ownerName && <div className="md:col-span-2 pt-1 mt-1 border-t border-emerald-200/70"><span className="font-semibold text-slate-700">Owner 1:</span> <span className="text-slate-800">{ownerName}</span></div>}
                    {pre.owner2?.primary_owner_name && <div className="md:col-span-2"><span className="font-semibold text-slate-700">Owner 2:</span> <span className="text-slate-800">{pre.owner2.primary_owner_name}</span></div>}
                    {pre.contact_name && <div><span className="font-semibold text-slate-700">Contact Name:</span> <span className="text-slate-800">{pre.contact_name}</span></div>}
                    {pre.industry_type && <div><span className="font-semibold text-slate-700">Industry Type:</span> <span className="text-slate-800">{pre.industry_type}</span></div>}
                    {pre.monthly_volume != null && pre.monthly_volume !== '' && <div><span className="font-semibold text-slate-700">Monthly Volume:</span> <span className="text-slate-800">${Number(pre.monthly_volume).toLocaleString()}</span></div>}
                    {pre.years_in_business && <div><span className="font-semibold text-slate-700">Years in Business:</span> <span className="text-slate-800">{pre.years_in_business}</span></div>}
                    {pre.pos_software_or_terminals && <div><span className="font-semibold text-slate-700">POS:</span> <span className="text-slate-800">{pre.pos_software_or_terminals}</span></div>}
                    {pre.additional_comments && <div className="md:col-span-2 pt-1 mt-1 border-t border-emerald-200/70"><span className="font-semibold text-slate-700">Comments:</span> <span className="text-slate-800 whitespace-pre-wrap">{pre.additional_comments}</span></div>}
                  </div>
                </CardContent>
              </Card>
            );
          })()
        )}

        {/* External Document Link (Google Drive / Dropbox / etc.) */}
        {data.document_link && (
          <Card data-testid="shared-document-link" className="border-cyan-200 bg-cyan-50/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-cyan-700" />
                External Document Link
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-600 mb-2">The submitting party shared this external link for additional documents:</p>
              <a
                href={/^https?:\/\//i.test(data.document_link) ? data.document_link : `https://${data.document_link}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-800 hover:text-cyan-900 underline break-all"
                data-testid="shared-document-link-anchor"
              >
                <ExternalLink className="h-4 w-4 shrink-0" />
                {data.document_link}
              </a>
            </CardContent>
          </Card>
        )}

        {/* Documents */}
        <Card data-testid="shared-documents">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle
                className="text-base flex items-center gap-2 cursor-pointer flex-1 min-w-0"
                onClick={() => toggleSection('docs')}
              >
                <FileText className="h-4 w-4 shrink-0" />
                <span className="truncate">Documents ({(data.documents?.length || 0) + (data.additional_documents?.length || 0)})</span>
                {expandedSections.docs === false ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
              </CardTitle>
              {((data.documents?.length || 0) + (data.additional_documents?.length || 0)) > 0 && (
                <Button
                  size="sm"
                  onClick={downloadAllAsZip}
                  disabled={downloadingAll}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  data-testid="download-all-zip-btn"
                  title="Download every uploaded document as one ZIP archive"
                >
                  {downloadingAll ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Preparing ZIP...</>
                  ) : (
                    <><Download className="h-4 w-4" /> Download All as ZIP</>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          {expandedSections.docs !== false && (
            <CardContent className="space-y-4">
              {data.documents?.length > 0 ? (() => {
                // Group documents by category
                const grouped = {};
                data.documents.forEach(doc => {
                  const cat = doc.category || 'Other';
                  if (!grouped[cat]) grouped[cat] = [];
                  grouped[cat].push(doc);
                });
                return Object.entries(grouped).map(([category, docs]) => (
                  <div key={category} className="space-y-1.5">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      {category}
                      <span className="text-xs text-slate-400 font-normal">{docs.length} file{docs.length > 1 ? 's' : ''}</span>
                    </p>
                    {docs.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors ml-6">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span className="text-sm text-slate-700 truncate">{doc.filename}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadDoc(doc.stored_filename || doc.id, doc.filename)}
                          disabled={downloadingDoc === (doc.stored_filename || doc.id)}
                          className="shrink-0 gap-1 text-xs h-7"
                          data-testid={`download-doc-${idx}`}
                        >
                          {downloadingDoc === (doc.stored_filename || doc.id) ? (
                            <><Loader2 className="h-3 w-3 animate-spin" /> Downloading...</>
                          ) : (
                            <><Download className="h-3 w-3" /> Download</>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                ));
              })() : (
                <p className="text-sm text-slate-400 text-center py-4">No documents uploaded yet</p>
              )}
              {data.additional_documents?.length > 0 && (
                <div className="pt-3 border-t mt-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    Additional Documents
                    <span className="text-xs text-slate-400 font-normal">{data.additional_documents.reduce((sum, cat) => sum + (cat.files?.length || 0), 0)} file{data.additional_documents.reduce((sum, cat) => sum + (cat.files?.length || 0), 0) !== 1 ? 's' : ''}</span>
                  </p>
                  {data.additional_documents.map((cat, catIdx) => (
                    <div key={catIdx} className="mb-2">
                      <p className="text-xs font-medium text-slate-500 mb-1 ml-2">{cat.name}</p>
                      {(cat.files || []).map((file, fileIdx) => (
                        <div key={fileIdx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg mb-1.5 hover:bg-slate-100 transition-colors ml-6">
                          <div className="flex items-center gap-2">
                            <FileText className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-sm text-slate-700">{file.filename || file.name}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDoc(file.gridfs_id || file.stored_filename, file.filename || file.name)}
                            disabled={downloadingDoc === (file.gridfs_id || file.stored_filename)}
                            className="gap-1 text-xs h-7"
                          >
                            {downloadingDoc === (file.gridfs_id || file.stored_filename) ? (
                              <><Loader2 className="h-3 w-3 animate-spin" /> Downloading...</>
                            ) : (
                              <><Download className="h-3 w-3" /> Download</>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Required Documents Checklist — read-only status overview */}
        {data.required_documents?.length > 0 && (
          <Card>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('reqDocs')}>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Required Documents Checklist
                {expandedSections.reqDocs === false ? <ChevronRight className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.reqDocs !== false && (
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">Status of each document required for this file.</p>
                <div className="space-y-1.5">
                  {data.required_documents.map((doc, idx) => {
                    const docName = doc.name;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm py-1 border-b border-slate-100 last:border-b-0" data-testid={`shared-checklist-row-${idx}`}>
                        {doc.received ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                        ) : (
                          <Clock className="h-4 w-4 text-amber-400 shrink-0" />
                        )}
                        <span className={`flex-1 ${doc.received ? 'text-slate-600' : 'text-slate-800 font-medium'}`}>{docName}</span>
                        <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md ${doc.received ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid={`shared-checklist-status-${idx}`}>
                          {doc.received ? 'Received' : 'Pending'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Approval Terms */}
        {data.approval_terms && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4" /> Approval Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.approval_terms}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-8 text-xs text-slate-400">
          <p>Quidity Direct - Confidential Underwriting File</p>
          <p className="mt-1">This link provides read-only access. Contact the underwriting team for questions.</p>
        </div>
      </div>
    </div>
  );
};

export default SharedUnderwritingView;
